import { CalendarClock } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api/client';
import type { Collection, CollectionSettings } from '../../api/types';
import { formatDate } from '../../format';
import { useI18n } from '../../i18n';
import { CollapsibleCard } from '../CollapsibleCard';
import { Field, PageLoader, Switch, useToast } from '../ui';

/** ISO time → value for <input type="datetime-local"> in the browser's zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);
const numberOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

interface Form {
  opensAt: string;
  closesAt: string;
  maxResponses: string;
  closeOnQuotasFull: boolean;
  inviteOnly: boolean;
  reminderDays: string;
  maxReminders: string;
}

const toForm = (c: Collection): Form => ({
  opensAt: toLocalInput(c.opensAt),
  closesAt: toLocalInput(c.closesAt),
  maxResponses: c.maxResponses == null ? '' : String(c.maxResponses),
  closeOnQuotasFull: c.closeOnQuotasFull,
  inviteOnly: c.inviteOnly,
  reminderDays: c.reminderDays == null ? '' : String(c.reminderDays),
  maxReminders: String(c.maxReminders),
});

/** When the survey opens and closes, when it closes itself, and who may answer. */
export function CollectionCard({ surveyId, canEdit, hasQuotas, onSaved }: { surveyId: string; canEdit: boolean; hasQuotas: boolean; onSaved?: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.collection(surveyId).then(
      (c) => {
        setCollection(c);
        setForm(toForm(c));
      },
      (e) => toast(e instanceof ApiError ? e.message : t('common.load_failed'), 'error'),
    );
  }, [surveyId, toast, t]);

  if (!form || !collection) {
    return (
      <div className="card">
        <PageLoader />
      </div>
    );
  }
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(collection));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    const body: CollectionSettings = {
      opensAt: fromLocalInput(form.opensAt),
      closesAt: fromLocalInput(form.closesAt),
      maxResponses: numberOrNull(form.maxResponses),
      closeOnQuotasFull: form.closeOnQuotasFull,
      inviteOnly: form.inviteOnly,
      reminderDays: numberOrNull(form.reminderDays),
      maxReminders: Number(form.maxReminders || 0),
    };
    setBusy(true);
    setErrors({});
    try {
      const c = await api.setCollection(surveyId, body);
      setCollection(c);
      setForm(toForm(c));
      toast(t('collection.saved'));
      onSaved?.();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        toast(err.message, 'error');
      } else toast(t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const saved = collection;
  const future = (iso: string | null) => !!iso && new Date(iso).getTime() > Date.now();
  const summary = [
    saved.opensAt ? t(future(saved.opensAt) ? 'collection.sum_opens' : 'collection.sum_opened', { date: formatDate(saved.opensAt, true) }) : '',
    saved.closesAt ? t('collection.sum_closes', { date: formatDate(saved.closesAt, true) }) : t('collection.sum_no_close'),
    saved.maxResponses != null ? t('collection.sum_max', { n: saved.maxResponses }) : '',
    saved.closeOnQuotasFull ? t('collection.sum_quotas') : '',
    saved.inviteOnly ? t('collection.sum_invite_only') : '',
    saved.reminderDays ? t('collection.sum_reminders', { n: saved.reminderDays }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <CollapsibleCard
      storageKey="survey.share.open.collection"
      defaultOpen={false}
      icon={<CalendarClock />}
      title={t('collection.title')}
      sub={summary}
      badge={saved.autoClosedAt ? <span className="pill orange">{t('collection.auto_closed_badge')}</span> : null}
    >
      <form className="card-pad stack gap-12" style={{ paddingTop: 0 }} onSubmit={(e) => void save(e)}>
        <p className="hint" style={{ margin: 0 }}>
          {t('collection.help')}
        </p>
        {collection.autoCloseReason && collection.autoClosedAt && (
          <div className="banner warn">{t(`progress.auto_closed_${collection.autoCloseReason}`, { date: formatDate(collection.autoClosedAt, true) })}</div>
        )}
        <fieldset disabled={!canEdit || busy} className="stack gap-12" style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="settings-fields">
            <Field label={t('collection.opens_at')} hint={t('collection.opens_hint')} error={errors.opensAt} htmlFor="col-opens">
              <input id="col-opens" className="input" type="datetime-local" value={form.opensAt} onChange={(e) => set({ opensAt: e.target.value })} />
            </Field>
            <Field label={t('collection.closes_at')} hint={t('collection.closes_hint')} error={errors.closesAt} htmlFor="col-closes">
              <input id="col-closes" className="input" type="datetime-local" value={form.closesAt} onChange={(e) => set({ closesAt: e.target.value })} />
            </Field>
          </div>
          <Field label={t('collection.max_responses')} hint={t('collection.max_responses_hint')} error={errors.maxResponses} htmlFor="col-max">
            <input
              id="col-max"
              className="input"
              type="number"
              min={1}
              inputMode="numeric"
              placeholder={t('collection.no_limit')}
              value={form.maxResponses}
              onChange={(e) => set({ maxResponses: e.target.value })}
            />
          </Field>
          <div className="switch-grid">
            <Switch
              checked={form.closeOnQuotasFull}
              onChange={(v) => set({ closeOnQuotasFull: v })}
              label={hasQuotas ? t('collection.close_on_quotas') : t('collection.close_on_quotas_none')}
              disabled={!canEdit || busy || (!hasQuotas && !form.closeOnQuotasFull)}
            />
            <Switch checked={form.inviteOnly} onChange={(v) => set({ inviteOnly: v })} label={t('collection.invite_only')} />
          </div>
          <div className="settings-fields">
            <Field
              label={t('collection.reminder_days')}
              hint={collection.emailEnabled ? t('collection.reminder_hint') : t('collection.reminder_no_email')}
              error={errors.reminderDays}
              htmlFor="col-remind"
            >
              <input
                id="col-remind"
                className="input"
                type="number"
                min={1}
                max={60}
                inputMode="numeric"
                placeholder={t('collection.no_reminders')}
                value={form.reminderDays}
                onChange={(e) => set({ reminderDays: e.target.value })}
              />
            </Field>
            <Field label={t('collection.max_reminders')} error={errors.maxReminders} htmlFor="col-max-remind">
              <input id="col-max-remind" className="input" type="number" min={0} max={10} inputMode="numeric" value={form.maxReminders} onChange={(e) => set({ maxReminders: e.target.value })} />
            </Field>
          </div>
        </fieldset>
        {canEdit && (
          <div className="card-foot">
            <span className="small muted">{dirty ? t('collection.unsaved') : ''}</span>
            <button className="btn primary" type="submit" disabled={busy || !dirty}>
              {t('collection.save')}
            </button>
          </div>
        )}
      </form>
    </CollapsibleCard>
  );
}
